import { Card } from "@coherence-design-system/controls";
import { FluentProvider, OptionOnSelectData, webLightTheme } from "@fluentui/react-components";
import { CardContent } from "@mui/material";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IArrayKey } from "../../../interfaces/CarouselProps.interfaces";
import { IMdcProductFamily, IProductKPIWithVariance } from "../../../interfaces/customer-product-v2.interfaces";
import { DropdownReducerType } from "../../../Utils/Enums/Customer.constants";
import { arrSortDescByKeyLength } from "../../../Utils/js-common";
import CustomDropdown from "../../ProductView/Common/CustomDropdown";
import {
  aggregateByCloudSet,
  dispatchLoadMenuItems,
  dispatchMenuItemsUpdate,
  dropdownReducer,
  getSelectedOrDefault,
  mapFilterPillMenuItemsPredicate,
  retrieveDataPoints
} from "./ProductTelemetry.helper";
import { ProductTelemetryChart } from "./ProductTelemetryChart";
import { ProductTelemetryDailyChart } from "./ProductTelemetryDailyChart";
import WarningBanner from "../../../helper/WarningBanner";

export default function MdcProductTelemetryView({ data = [] }: { data: IMdcProductFamily[] }) {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id") ?? undefined;
  const [sortedData, setSortedData] = useState<IMdcProductFamily[]>([]);
  const defaultFamily = "Microsoft Defender for Cloud";
  const defaultCloudType = "AllUp";
  const defaultAggregationType = "Monthly";
  const cloudAggregationMapping: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
    Object.entries(aggregateByCloudSet(retrieveDataPoints(data ?? []))).map(([c, set]) => [c, Array.from(set)])
  );
  const mapCloudAggregationToDropdown = () =>
    Object.keys(cloudAggregationMapping).map((cloudType) => ({
      value: cloudType,
      text: cloudType
    }));
  const mapAggregationTypeToDropdown = () =>
    Array.from(new Set(Object.values(cloudAggregationMapping).flat())).map((cloudType) => ({
      value: cloudType,
      text: cloudType
    }));
  const [cloudTypeState, cloudTypeDispatch] = useReducer(dropdownReducer, {
    options: [],
    selectedItem: defaultCloudType
  });
  const [aggregationTypeState, aggregationTypeDispatch] = useReducer(dropdownReducer, {
    options: [],
    selectedItem: defaultAggregationType
  });
  // Memoized selected values
  const cloudAggregationOptions = useMemo(mapCloudAggregationToDropdown, [data]);
  const aggregationTypeOptions = useMemo(mapAggregationTypeToDropdown, [data]);
  const selectedCloudType = useMemo(() => getSelectedOrDefault(cloudTypeState), [cloudTypeState]);
  const selectedAggregationType = useMemo(() => getSelectedOrDefault(aggregationTypeState), [aggregationTypeState]);

  // Events
  const dispatchCloudTypeLoad = dispatchLoadMenuItems(cloudTypeDispatch);
  const dispatchCloudTypeUpdate = dispatchMenuItemsUpdate(cloudTypeDispatch);
  const dispatchAggregationTypeLoad = dispatchLoadMenuItems(aggregationTypeDispatch);
  const dispatchAggregationTypeUpdate = dispatchMenuItemsUpdate(aggregationTypeDispatch);
  const handleSelectedGranularity = useCallback(
    ({ optionText }: OptionOnSelectData) => dispatchCloudTypeUpdate(optionText),
    []
  );
  const handleSelectedAggregationType = useCallback(
    ({ optionText }: OptionOnSelectData) => dispatchAggregationTypeUpdate(optionText),
    []
  );

  // Hooks
  useEffect(() => {
    dispatchCloudTypeLoad(cloudAggregationOptions);
    dispatchCloudTypeUpdate(defaultCloudType);
    dispatchAggregationTypeLoad(aggregationTypeOptions);
    dispatchAggregationTypeUpdate(defaultAggregationType);
  }, [data]);

  useEffect(() => {
    const filteredAggregationTypes = cloudAggregationMapping[cloudTypeState.selectedItem] || [];
    aggregationTypeDispatch({
      type: DropdownReducerType.LoadItems,
      data: filteredAggregationTypes.map(mapFilterPillMenuItemsPredicate)
    });
  }, [cloudTypeState.selectedItem]);

  useEffect(() => {
    if (data) {
      setSortedData(
        arrSortDescByKeyLength({
          key: "data",
          data: JSON.parse(
            JSON.stringify(
              data.map((group) => ({
                ...group,
                data: group.data.map((series) => ({
                  ...series,
                  data: series.data
                    .map((m) => ({
                      ...m,
                      data: m.data.filter(
                        (p) => p.aggregationType == selectedAggregationType && p.cloud == selectedCloudType
                      )
                    }))
                    .filter((f) => f.data.length)
                }))
              }))
            )
          )
        } as IArrayKey)
      );
    }
  }, [data, selectedCloudType, selectedAggregationType]);

  return (
    sortedData.length > 0 && (
      <div className="p-4">
        <WarningBanner message="Note: MDC monthly usage is shown as a rolling 28-day average based on days with actual usage." />
        <FluentProvider theme={webLightTheme}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", padding: "0 10px" }}>
            <CustomDropdown
              defaultOption={{
                optionText: cloudTypeState.selectedItem,
                optionValue: cloudTypeState.selectedItem,
                selectedOptions: [cloudTypeState.selectedItem || ""]
              }}
              onSelectedOption={handleSelectedGranularity}
              data={cloudTypeState.options}
              styles={{ root: { minWidth: 100, flexBasis: "auto" } }}
              label="Cloud Type"
            />
            <CustomDropdown
              defaultOption={{
                optionText: aggregationTypeState.selectedItem,
                optionValue: aggregationTypeState.selectedItem,
                selectedOptions: [aggregationTypeState.selectedItem || ""]
              }}
              onSelectedOption={handleSelectedAggregationType}
              data={aggregationTypeState.options}
              styles={{ root: { minWidth: 100, flexBasis: "auto" } }}
              label="Aggregation Type"
            />
          </div>
        </FluentProvider>
        <Card>
          <CardContent>
            {sortedData.map(({ name: pName, data: pData }) => {
              const chartProps = {
                tenantId,
                disableExploreFeaturesButton: true,
                granularity: selectedAggregationType,
                cloud: selectedCloudType,
                selectedFamily: defaultFamily,
                application: undefined,
                carouseldata: {
                  name: "",
                  items: pData.map(({ name, data, kustoLink, isCAT, isTCT, hasEngagedAdmin }) => {
                    return {
                      name,
                      data: data as IProductKPIWithVariance[],
                      isTCT: isTCT,
                      isCAT: isCAT,
                      hasEngagedAdmin: hasEngagedAdmin,
                      kustoLink
                    };
                  })
                }
              };
              return selectedAggregationType === "Daily" ? (
                <ProductTelemetryDailyChart
                  key={`daily-${pName}-${selectedAggregationType}`}
                  {...chartProps}
                  disableVariances
                />
              ) : (
                <ProductTelemetryChart key={`monthly-${pName}-${selectedAggregationType}`} {...chartProps} />
              );
            })}
          </CardContent>
        </Card>
      </div>
    )
  );
}
